import {
  Button as RacButton,
  FieldError,
  Group,
  Input,
  Label,
  Text,
  SearchField,
  TextField as TextFieldBase,
  type TextFieldProps as TextFieldBaseProps,
  type SearchFieldProps,
} from "react-aria-components";
import { css, cx, type RecipeVariantProps } from "../../../../styled-system/css";
import { FaMagnifyingGlass, FaPencil, FaX } from "react-icons/fa6";
import { inputClassNames } from "../styles/styles";
import { useState } from "react";
import { Modal } from "../../core/basic/RACModal";
import { TextAreaField } from "./TextAreaField";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../core/basic/Button";

interface SearchInputProps extends SearchFieldProps {
  label?: string;
  errorMessage?: string;
  description?: string;
  placeholder?: string;
  className?: string;
  isExpandable?: boolean;
  variant?: Exclude<RecipeVariantProps<typeof inputClassNames>, undefined>["variant"];
}

export const SearchInput = ({
  value,
  onChange,
  label,
  errorMessage,
  description,
  onSubmit,
  onClear,
  className,
  isExpandable,
  variant,
  ...rest
}: SearchInputProps) => {
  const classes = inputClassNames({ variant });
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <SearchField
      className={cx(classes.fieldWrapper, className)}
      value={value}
      onChange={onChange}
      onClear={onClear}
      onSubmit={onSubmit}
      isInvalid={!!errorMessage}
      {...rest}
    >
      {({ isEmpty, state }) => (
        <>
          {label && <Label className={classes.label}>{label}</Label>}
          <Group className={classes.inputBox}>
            <FaMagnifyingGlass className={css({ fontSize: "medium" })} strokeWidth={"15"} />
            <Input
              className={cx(
                classes.input,
                css({
                  "&::-webkit-search-cancel-button,&::-webkit-search-decoration": {
                    WebkitAppearance: "none",
                  },
                }),
              )}
            />

            {!isEmpty && (
              <RacButton
                className={css({
                  all: "unset",
                  cursor: "pointer",
                  color: "#aaa",
                  fontSize: "small",
                  "&[data-focus-visible='true']": {
                    color: "white",
                    backgroundColor: "#aaa",
                  },
                })}
              >
                <FaX strokeWidth={"20"} />
              </RacButton>
            )}
            {isExpandable && (
              <RacButton
                slot={null}
                className={css({
                  all: "unset",
                  cursor: "pointer",
                  color: "#aaa",
                  fontSize: "small",
                  "&[data-focus-visible='true']": {
                    color: "white",
                    backgroundColor: "#aaa",
                  },
                })}
                onPress={() => setIsExpanded(!isExpanded)}
              >
                <FaPencil strokeWidth={"20"} />
              </RacButton>
            )}
          </Group>
          {description && <Text slot="description">{description}</Text>}
          {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
          {isExpanded && (
            <SearchFieldExpanded
              initialValue={state.value}
              onApply={(v) => state.setValue(v)}
              isOpen={isExpanded}
              setIsOpen={setIsExpanded}
            />
          )}
        </>
      )}
    </SearchField>
  );
};

interface TextFieldProps extends TextFieldBaseProps {
  label?: React.ReactNode;
  description?: string;
  errorMessage?: string;
  className?: string;
  variant?: Exclude<RecipeVariantProps<typeof inputClassNames>, undefined>["variant"];
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  placeholder?: string;
}

export function TextField({
  label,
  description,
  errorMessage,
  className,
  variant,
  startIcon,
  endIcon,
  placeholder,
  ...props
}: TextFieldProps) {
  const classes = inputClassNames({ variant });
  return (
    <TextFieldBase isInvalid={!!errorMessage} {...props} className={cx(classes.fieldWrapper, className)}>
      {label && <Label className={classes.label}>{label}</Label>}
      <Group
        className={cx(
          classes.inputBox,
          // Workaround for https://github.com/adobe/react-spectrum/issues/7451
          props.isDisabled ?
            css({
              "&&&": { cursor: "not-allowed", backgroundColor: "lightgrey" },
            })
          : undefined,
        )}
      >
        {startIcon && (
          <span
            className={css({
              fontSize: "medium",
              fontWeight: "300",
            })}
          >
            {startIcon}
          </span>
        )}
        <Input className={classes.input} placeholder={placeholder} />
        {endIcon && (
          <span
            className={css({
              fontSize: "medium",
              fontWeight: "300",
            })}
          >
            {endIcon}
          </span>
        )}
      </Group>
      {description && <Text slot="description">{description}</Text>}
      {errorMessage && <FieldError className={classes.error}>{errorMessage}</FieldError>}
    </TextFieldBase>
  );
}

function SearchFieldExpanded({
  initialValue,
  onApply,
  isOpen,
  setIsOpen,
}: {
  initialValue?: string;
  onApply: (value: string) => void;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}) {
  const [value, setValue] = useState(
    initialValue
      ?.split(",")
      .map((v) => v.trim())
      .filter(Boolean)
      .join("\n") ?? "",
  );

  return (
    <Modal isOpen={isOpen} onOpenChange={setIsOpen} title={"Search"} className={css({ width: "75vw" })}>
      <VStack alignItems={"stretch"} paddingX={"large"}>
        <TextAreaField value={value} onChange={setValue} description={"Separate text using commas or new lines"} />
        <HStack gap={"medium"} justifyContent={"center"}>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            onPress={() => {
              onApply(
                value
                  .split("\n")
                  .map((v) => v.trim())
                  .filter(Boolean)
                  .join(","),
              );
              setIsOpen(false);
            }}
          >
            Apply
          </Button>
        </HStack>
      </VStack>
    </Modal>
  );
}
